/*
 * Shared filter vocabulary for the Sales Task List feature — the option lists,
 * the defaults, the localStorage-backed initial state and the query string the
 * API expects.
 *
 * Extracted from index.jsx (the list page) when the task form started needing the
 * same filters: "Done and Next" asks the server for the next task, and that walk
 * has to stay inside the set the agent was browsing — otherwise a concierge
 * filtered to their own list is handed somebody else's task. Both pages build the
 * query here so the two can never drift.
 *
 * The persistence layer itself lives in filters_storage.js; this module owns what
 * the values MEAN (defaults, option lists, wire format).
 */

import { read_persisted_filters } from './filters_storage';
import { SCOPE_VALUES, normalize_scope } from './level';
import { dayInTz, resolveFilterTz } from './filter_tz';

// Sentinel "Assigned to" option value for tasks with no assignee. A real
// cms_user uuid never collides with it. Mirrors unassignedFilterValue in
// goserver's api_list.go.
export const UNASSIGNED_VALUE = '__unassigned__';

// Default due window: yesterday → tomorrow in tz (the header timezone, sent as
// ?tz=; unset = IST like the server) — what slipped, what's due now, what's next.
export const default_date_range = (tz) => ({
  from: dayInTz(tz, -1),
  to: dayInTz(tz, 1)
});

// Assignee scope for /next_task when the form was not opened from the list. The
// list itself filters by scope/owner instead.
export const default_agent_filter = (self_uuid) =>
  self_uuid ? [UNASSIGNED_VALUE, self_uuid] : [UNASSIGNED_VALUE];

// Status filter tabs. Sales tasks have no draft stage in practice — just
// pending → submitted (completed) — though the status column can hold 'draft',
// and the server folds that into "Pending" ("not submitted").
//
// "Overdue" is not a stored status: it is pending AND past due_datetime,
// evaluated server-side against UTC now (see statusClause in db.go). It sits
// second because it is the subset of Pending that needs attention first.
export const STATUS_FILTERS = [
  { label: 'All', value: 'All' },
  { label: 'Overdue', value: 'Overdue' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Submitted', value: 'Submitted' },
  { label: 'Skipped', value: 'Skipped' }
];

// Task-type filter options, in the same order as the config cards. There are
// more than a dozen and the list grows with every new rule, which is why this is
// a multi-select dropdown rather than the pill bar it started as — the bar
// overflowed the filter row. An EMPTY selection
// means "all types" (the same convention the assignee dropdown uses), so there is
// no 'All' option to pick.
//
// This array doubles as the allow-list for persisted filters (TYPE_VALUES feeds
// read_persisted_filters), so a type missing here is silently stripped from every
// saved filter — which hides its tasks from any agent who has one.
export const TYPE_OPTIONS = [
  { label: 'Fresh lead', value: 'FRESH_LEAD' },
  { label: 'Upcoming trial session', value: 'UPCOMING_TRIAL_SESSION' },
  { label: 'Unanswered call', value: 'UNANSWERED_INCOMING_CALL' },
  { label: 'Unanswered SMS', value: 'UNANSWERED_INCOMING_SMS' },
  { label: 'Pricing page visited', value: 'PRICING_PAGE_VISITED' },
  { label: '3 month plan opened', value: 'THREE_MONTH_PLAN_OPENED' },
  { label: 'No book', value: 'NO_BOOK' },
  { label: 'Book, no join', value: 'BOOK_NO_JOIN' },
  { label: 'Join, no membership', value: 'JOIN_NO_MEMBERSHIP' },
  { label: 'No trial reset', value: 'LOGIN_NO_TRIAL_RESET' },
  { label: 'Reset, no book', value: 'TRIAL_RESET_NO_BOOK' },
  { label: 'Reset, booked, no join', value: 'TRIAL_RESET_BOOK_NO_JOIN' },
  { label: 'Reset, joined, no membership', value: 'TRIAL_RESET_JOIN_NO_MEMBERSHIP' },
  { label: 'Lead - OTP not verified', value: 'LEAD_OTP_NOT_VERIFIED' },
  { label: 'Churned member login', value: 'CHURNED_MEMBER_LOGIN' },
  // Wave 7 friction rules. All event-driven (no day offset), which is why they
  // also appear in rule_line.js's NO_DAY_TASK_TYPES — the two lists have to move
  // together or the list page prints a meaningless "Day 0".
  { label: 'Login issue', value: 'LOGIN_ISSUE' },
  { label: 'Booking skipped in onboarding', value: 'BOOKING_SKIPPED_ONBOARDING' },
  { label: 'OTP verification failed', value: 'OTP_VERIFICATION_FAILED' },
  { label: 'Onboarding idle timeout', value: 'ONBOARDING_IDLE_TIMEOUT' },
  { label: 'Onboarding drop-off', value: 'ONBOARDING_DROP_OFF' },
  { label: 'Signup not completed', value: 'SIGNUP_NOT_COMPLETED' },
  { label: 'No booking slots available', value: 'NO_BOOKING_SLOTS' },
  { label: 'Checkout drop-off', value: 'CHECKOUT_DROP_OFF' },
  { label: 'Slots viewed, not booked', value: 'SLOTS_VIEWED_NOT_BOOKED' },
  { label: 'Pre-session no-join', value: 'PRE_SESSION_NO_JOIN' },
  { label: 'Reminder link no-join', value: 'REMINDER_LINK_NO_JOIN' },
  { label: 'Reminder reply', value: 'REMINDER_REPLY' },
  { label: 'Booking error', value: 'BOOKING_ERROR' },
  // Wave 4 session / payment / retention rules: event or sweep driven, so they
  // are in NO_DAY_TASK_TYPES too.
  { label: 'Session joining & closing', value: 'SESSION_JOIN_CLOSE' },
  { label: 'Joining call, 15 min before session', value: 'PRE_SESSION_JOIN_CALL' },
  { label: 'Backup session join', value: 'BACKUP_SESSION_JOIN' },
  { label: 'Trial payment failure', value: 'TRIAL_PAYMENT_FAILED' },
  { label: 'No show / cancellation call', value: 'NO_SHOW_CANCEL_CALL' },
  { label: 'Reactivation', value: 'REACTIVATION' },
  { label: 'Churned member booked', value: 'CHURNED_MEMBER_BOOKED' },
  { label: 'Dropped off while paying', value: 'PAYMENT_DROP_OFF_POST_SESSION' }
];

// Call-eligibility filter ('' = any). The server judges eligibility against
// the configured call window in the user's local time, and excludes rows with
// no known timezone from both choices.
export const CALL_FILTERS = [
  { label: 'Eligible to call', value: 'eligible' },
  { label: 'Not eligible', value: 'not_eligible' }
];

export const STATUS_VALUES = STATUS_FILTERS.map((o) => o.value);
export const CALL_VALUES = CALL_FILTERS.map((o) => o.value);
export const TYPE_VALUES = TYPE_OPTIONS.map((o) => o.value);

/*
 * Initial state of the persisted filters (status, type, scope, owner, date
 * range). Saved choices win over the built-in defaults — including a
 * deliberately cleared one, which is stored as an empty selection and must not
 * be re-seeded. A filter that was never saved, or whose saved value no longer
 * validates, falls back to its default individually, so one stale value can't
 * discard the rest.
 *
 * The date range is the exception: it is restored only when the user explicitly
 * picked or cleared it (date_range non-null in storage). Left untouched, it
 * re-seeds from today so the window keeps rolling instead of restoring last
 * week's absolute dates.
 *
 * The list page holds this in component state; the form page resolves it fresh
 * (read-only) to reproduce the filters the agent was browsing with.
 */
export const initial_filter_state = (tz) => {
  const rolling = default_date_range(tz);
  const state = {
    status_filter: 'Pending',
    // [] = every task type.
    type_filter: [],
    scope_filter: 'all',
    // L3 only; [] = every owner.
    owner_filter: [],
    // '' = any; else a CALL_VALUES entry.
    call_filter: '',
    date_from: rolling.from,
    date_to: rolling.to,
    // False while the range is still the rolling default; only an explicit pick
    // or clear is persisted (see filters_storage.js).
    date_range_touched: false
  };

  const saved = read_persisted_filters({
    status_values: STATUS_VALUES,
    type_values: TYPE_VALUES,
    scope_values: SCOPE_VALUES,
    call_values: CALL_VALUES
  });
  if (!saved) return state;

  if (saved.status_filter !== undefined) state.status_filter = saved.status_filter;
  if (saved.type_filter !== undefined) state.type_filter = saved.type_filter;
  if (saved.scope_filter !== undefined) state.scope_filter = saved.scope_filter;
  if (saved.owner_filter !== undefined) state.owner_filter = saved.owner_filter;
  if (saved.call_filter !== undefined) state.call_filter = saved.call_filter;
  if (saved.date_range) {
    state.date_from = saved.date_range.from;
    state.date_to = saved.date_range.to;
    state.date_range_touched = true;
  }
  return state;
};

/*
 * Serialise the filters into the query params both /list and /next_task read
 * (status, task_type, date_from, date_to, tz, assigned, level, scope, owner,
 * call, search). tz is the zone the date days are in; omitted = IST server-side.
 * level is not persisted; the list passes its current level and the form reads
 * it from ?level=. Sorting and pagination are list-only and stay with the
 * caller — the same split the server makes in listFiltersFromQuery.
 *
 * Omitting a param means "no filter on it", which is why the empty selections are
 * simply left out rather than sent as an explicit "All".
 *
 * search is the type-to-search box on the list page: a prefix match on the
 * student's first name, resolved server-side (the table is server-paginated, so
 * a client-side filter would only ever search the current 20-row page). It is
 * NOT persisted to localStorage — a search is momentary, not a working
 * preference — so unlike the persisted filters it cannot reach the task form
 * through storage. The list instead puts an active search term in the "Open Task"
 * href (?search=…) and the form reads it back, so "Done and Next" walks the
 * searched set rather than silently widening to the unsearched one.
 */
export const filter_query_params = ({
  status_filter,
  type_filter = [],
  date_from,
  date_to,
  agent_filter = [],
  level = '',
  scope_filter = '',
  owner_filter = [],
  call_filter = '',
  search = '',
  tz = ''
} = {}) => {
  const params = new URLSearchParams();
  if (status_filter) params.set('status', status_filter.toLowerCase());
  // Comma-separated list; the API also accepts a repeated param and a single
  // bare value (the pre-multi-select form).
  if (type_filter.length) params.set('task_type', type_filter.join(','));
  if (date_from) params.set('date_from', date_from);
  if (date_to) params.set('date_to', date_to);
  // Resolved so the server reads the same zone the defaults were computed in.
  if (tz) params.set('tz', resolveFilterTz(tz));
  if (agent_filter.length) params.set('assigned', agent_filter.join(','));
  if (level) params.set('level', level);
  // My students and the owner filter only exist at L3; a persisted value from an
  // L3 visit must not narrow an L1/L2 query.
  const scope = scope_filter ? normalize_scope(scope_filter, level) : 'all';
  if (scope !== 'all') params.set('scope', scope);
  if (level === 'L3' && owner_filter.length) params.set('owner', owner_filter.join(','));
  if (CALL_VALUES.includes(call_filter)) params.set('call', call_filter);
  // The server trims and LIKE-escapes it; trimming here too keeps a stray space
  // from making the request look filtered when it isn't.
  if (search && search.trim()) params.set('search', search.trim());
  return params;
};
